import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, Lock, X } from "lucide-react";
import { Report } from "@rti/shared";


interface DashboardGridProps {
  reportsGroupings: {
    group1: Report[];
    group2?: Report[];
  };
  gradients: string[];
  textColors: string[];
  hasPremiumAccess: boolean;
  useResinDashboards: boolean;
  onNavigate: (path: string) => void;
  loading: boolean;
}

const DashboardGrid: React.FC<DashboardGridProps> = ({
  reportsGroupings,
  gradients,
  textColors,
  hasPremiumAccess,
  useResinDashboards,
  onNavigate,
  loading
}) => {
  const getMarketIntelPath = (report: Report): string => {
    if (!useResinDashboards) return `/marketIntel/${report.report_id}`;
    const abbr = report.abbreviation;
    const key = (abbr || '').toUpperCase();
    if (key === 'PC' || key === 'PETCHEM') return '/marketIntel/petchem';
    return `/marketIntel/${key.toLowerCase()}`;
  };

  const [lockedPopoverId, setLockedPopoverId] = useState<string | null>(null);
  const [isFading, setIsFading] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      // cleanup timers on unmount
      if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
      if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    };
  }, []);

  const clearTimers = () => {
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (fadeTimeoutRef.current) {
      window.clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
  };

  const beginFade = () => {
    setIsFading(true);
    // After fade transition, fully hide
    fadeTimeoutRef.current = window.setTimeout(() => {
      setLockedPopoverId(null);
      setIsFading(false);
    }, 200);
  };

  const showPopover = (id: string) => {
    clearTimers();
    setLockedPopoverId(id);
    setIsFading(false);
    // Auto-hide after 2 seconds
    hideTimeoutRef.current = window.setTimeout(() => {
      beginFade();
    }, 5000);
  };

  const closePopover = () => {
    clearTimers();
    setLockedPopoverId(null);
    setIsFading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center h-full w-full justify-center">
        <RotateCw className="spinner opacity-30" size={75} strokeWidth={1}/>
      </div>
    );
  }

  let continuousIndex = 0;
  return (
    <div className="grid-cols-1 lg:grid-cols-2 gap-8 md:max-h-[500px] md:h-full h-auto w-full lg:col-span-1 sm:col-span-2">
      {Object.values(reportsGroupings).filter(Boolean).map((grouping, groupIndex) => (
        <div
          key={groupIndex}
          className={`grid gap-4 h-full md:grid-cols-2 sm:grid-cols-1  ${
            grouping.length <= 2 ? "lg:grid-cols-2 mb-5" : "lg:grid-cols-3"
          }`}
        >
          {grouping.map(
            (
              report: Report,
            ) => {
              if (!report.is_active) return null;
              // Use continuousIndex for color selection
              const gradientClass = `dashboard-${gradients[continuousIndex % gradients.length]}-gradient`;
              const buttonColorClass = `translucent-${gradients[continuousIndex % gradients.length]}`;
              const textColorClass = `text-rs-${textColors[continuousIndex % textColors.length]}`;
              const currentIndex = continuousIndex;
              continuousIndex++;
              const id = String(report.report_id);
              const forecastLocked = !hasPremiumAccess && !!report.has_forecast;

              return (
                <div
                  key={currentIndex}
                  className={`rounded-xl pt-5 px-5 cursor-default relative flex flex-col items-center justify-center min-h-[400px] max-h-[500px] w-full h-full min-w-[220px] dashboard-polymer-card ${gradientClass}`}
                >
                  {/* Title */}
                  <div className="text-center w-full flex justify-center items-end h-[50%]">
                    <h2 className={`text-4xl font-bold ${textColorClass}`}>
                      {report.abbreviation}
                    </h2>
                  </div>

                  {/* Buttons */}
                  <div className="mt-10 w-full px-3 flex flex-col justify-center items-end h-[50%]">
                    <button
                      data-testid="market-intel-button"
                      className={`w-full !py-3 rounded-md text-white text-lg font-semibold dashboard-button mb-3 ${buttonColorClass} hover:opacity-90 transition-opacity`}
                      onClick={() =>
                        onNavigate(
                          getMarketIntelPath(report),
                        )
                      }
                    >
                      Market Intel
                    </button>

                    {report.has_acc && (
                      <button
                        data-testid="monthly-snapshot-button"
                        className={`w-full !py-3 rounded-md text-white text-lg font-semibold dashboard-button mb-3 translucent-white hover:opacity-90 ${buttonColorClass}`}
                        onClick={() =>
                          onNavigate(
                            `/snapshot/${report.abbreviation.toLowerCase()}`,
                          )
                        }
                      >
                        Monthly Snapshot
                      </button>
                    )}

                    {report.has_forecast && (
                      <div
                        className="relative w-full"
                        onMouseEnter={() => forecastLocked && showPopover(id)}
                        onMouseLeave={() => forecastLocked && beginFade()}
                      >
                        <button
                          data-testid="pricing-forecast-button"
                          className={`w-full !py-3 rounded-md text-white text-lg font-semibold dashboard-button mb-3 translucent-white ${buttonColorClass} ${
                            hasPremiumAccess
                              ? "hover:opacity-90"
                              : "opacity-50 !cursor-not-allowed"
                          }`}
                          onClick={(e) => {
                            if (hasPremiumAccess) {
                              onNavigate(
                                `/forecast/${report.abbreviation.toLowerCase()}`,
                              );
                            } else {
                              e.preventDefault();
                              e.stopPropagation();
                              showPopover(id);
                            }
                          }}
                          aria-describedby={forecastLocked ? `premium-popover-${id}` : undefined}
                          aria-haspopup={forecastLocked ? "dialog" : undefined}
                        >
                          {/* Lock Icon in top-right when locked */}
                          {!hasPremiumAccess && (
                            <span className="absolute right-3 top-2 inline-flex items-center justify-center">
                              <Lock className="w-4 h-4 text-yellow-500" />
                            </span>
                          )}
                          Pricing &amp; Forecast
                        </button>

                        {/* Popover */}
                        {forecastLocked && lockedPopoverId === id && (
                          <div
                            id={`premium-popover-${id}`}
                            role="dialog"
                            aria-live="polite"
                            className={`absolute z-20 right-0 -top-2 translate-y-[-100%] mb-2 w-72 rounded-lg border border-black/10 bg-white text-rs-off-black shadow-xl p-3 transition-opacity duration-1000 ${
                              isFading ? 'opacity-0 pointer-events-none' : 'opacity-100'
                            }`}
                            onMouseEnter={() => {
                              // keep it open while hovered
                              clearTimers();
                              setIsFading(false);
                            }}
                            onMouseLeave={() => beginFade()}
                          >
                            <div className="flex items-start gap-2">
                              <div className="mt-0.5">
                                <Lock className="w-5 h-5 text-yellow-600" />
                              </div>
                              <div className="flex-1 text-sm leading-snug">
                                <p className="font-semibold mb-1 text-xl">Premium feature</p>
                                <p>
                                  <strong>Compound Plan</strong> or higher is required to access our Pricing & Forecast.

                                </p>
                              </div>
                              <button
                                className="ml-2 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10"
                                aria-label="Close"
                                onClick={closePopover}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            },
          )}
        </div>
      ))}
    </div>
  );
};

export default DashboardGrid;
